import {
  AnimalType,
  MedType,
  IndicationType,
  WeightUnit,
  DosageCalculation,
  DosageRegimen
} from '../types/dosage-types';
import { getDosageRegimen, getMedicationInfo } from '../data/dosage-data';

export class DosageCalculatorService {
  /**
   * Convert weight to kilograms
   */
  private static convertToKg(weight: number, unit: WeightUnit): number {
    switch (unit) {
      case WeightUnit.KG:
        return weight;
      case WeightUnit.LBS:
        return weight * 0.453592;
      case WeightUnit.GRAMS:
        return weight / 1000;
      default:
        return weight;
    }
  }

  /**
   * Validate weight against regimen constraints
   */
  private static validateWeight(weightKg: number, regimen: DosageRegimen): string[] {
    const warnings: string[] = [];

    if (regimen.minWeightKg && weightKg < regimen.minWeightKg) {
      warnings.push(`Weight is below minimum recommended weight of ${regimen.minWeightKg}kg`);
    }

    if (regimen.maxWeightKg && weightKg > regimen.maxWeightKg) {
      warnings.push(`Weight is above maximum recommended weight of ${regimen.maxWeightKg}kg`);
    }

    return warnings;
  }

  /**
   * Generate frequency description
   */
  private static getFrequencyDescription(regimen: DosageRegimen): string {
    const hoursToReadable = (hours: number): string => {
      if (hours === 24) return 'once daily';
      if (hours === 12) return 'twice daily (every 12 hours)';
      if (hours === 8) return 'three times daily (every 8 hours)';
      if (hours === 6) return 'four times daily (every 6 hours)';
      return `every ${hours} hours`;
    };

    const frequency = hoursToReadable(regimen.doseFrequencyHours);

    if (regimen.regimenType === 'single') {
      return 'Single dose';
    }

    return `${frequency} for ${regimen.regimenFrequency} ${regimen.regimenFrequency === 1 ? 'day' : 'days'}`;
  }

  /**
   * Calculate withdrawal date
   */
  private static getWithdrawalDate(regimen: DosageRegimen): Date | undefined {
    if (!regimen.withdrawalPeriodDays) return undefined;

    const today = new Date();
    const treatmentDuration = Math.ceil(regimen.regimenFrequency * regimen.doseFrequencyHours / 24);
    const withdrawalStart = new Date(today.getTime() + treatmentDuration * 24 * 60 * 60 * 1000);
    return new Date(withdrawalStart.getTime() + regimen.withdrawalPeriodDays * 24 * 60 * 60 * 1000);
  }

  /**
   * Main calculation method
   */
  static calculate(
    animalType: AnimalType,
    medType: MedType,
    indicationType: IndicationType,
    weight: number,
    weightUnit: WeightUnit = WeightUnit.KG
  ): DosageCalculation {
    const weightKg = this.convertToKg(weight, weightUnit);
    const regimen = getDosageRegimen(animalType, medType, indicationType);
    const medication = getMedicationInfo(medType);

    const errors: string[] = [];
    const warnings: string[] = [];

    // Validation
    if (!regimen) {
      errors.push(`No dosage regimen found for ${medType} in ${animalType} for ${indicationType}`);
    }

    if (!medication) {
      errors.push(`No medication information found for ${medType}`);
    }

    if (weightKg <= 0) {
      errors.push('Weight must be greater than 0');
    }

    // If we have critical errors, return early
    if (errors.length > 0 || !regimen || !medication) {
      return {
        animalType,
        medType,
        indicationType,
        weightKg,
        regimen: regimen || {} as DosageRegimen,
        medication: medication || {} as any,
        regularDoseMg: 0,
        totalDoses: 0,
        frequencyDescription: '',
        isValid: false,
        warnings,
        errors
      };
    }

    // Weight validation
    warnings.push(...this.validateWeight(weightKg, regimen));

    // Add medication warnings
    warnings.push(...medication.warnings);

    // Add regimen contraindications
    if (regimen.contraindications) {
      warnings.push(...regimen.contraindications);
    }

    // Calculate doses in mg
    const regularDoseMg = weightKg * regimen.mgPerKgRegularDose;
    const loadingDoseMg = regimen.mgPerKgLoadingDose ? weightKg * regimen.mgPerKgLoadingDose : undefined;

    // Calculate liquid doses (ml)
    const regularDoseMl = medication.concentrationMgPerMl ? regularDoseMg / medication.concentrationMgPerMl : undefined;
    const loadingDoseMl = medication.concentrationMgPerMl && loadingDoseMg ? loadingDoseMg / medication.concentrationMgPerMl : undefined;

    // Calculate tablet doses
    const regularDoseTablets = medication.tabletStrengthMg ? regularDoseMg / medication.tabletStrengthMg : undefined;
    const loadingDoseTablets = medication.tabletStrengthMg && loadingDoseMg ? loadingDoseMg / medication.tabletStrengthMg : undefined;

    // Calculate total doses and schedule
    const totalDoses = regimen.regimenFrequency;
    const frequencyDescription = this.getFrequencyDescription(regimen);
    const withdrawalDate = this.getWithdrawalDate(regimen);

    return {
      animalType,
      medType,
      indicationType,
      weightKg,
      regimen,
      medication,

      regularDoseMg,
      loadingDoseMg,
      regularDoseMl,
      loadingDoseMl,
      regularDoseTablets,
      loadingDoseTablets,

      totalDoses,
      frequencyDescription,
      withdrawalDate,

      isValid: errors.length === 0,
      warnings,
      errors
    };
  }

  /**
   * Format dose for display
   */
  static formatDose(value: number | undefined, precision = 2): string {
    if (value === undefined) return 'N/A';

    if (value < 0.01) {
      return value.toFixed(4);
    } else if (value < 1) {
      return value.toFixed(3);
    } else if (value < 10) {
      return value.toFixed(precision);
    } else {
      return value.toFixed(1);
    }
  }

  /**
   * Get available combinations
   */
  static getAvailableCombinations(): Array<{
    animalType: AnimalType;
    medType: MedType;
    indicationType: IndicationType;
    displayName: string;
  }> {
    const combinations: Array<{
      animalType: AnimalType;
      medType: MedType;
      indicationType: IndicationType;
      displayName: string;
    }> = [];

    // This would iterate through the dosageData to find all valid combinations
    // For now, returning some common ones
    return [
      {
        animalType: AnimalType.DOG,
        medType: MedType.TOLTRAZURIL,
        indicationType: IndicationType.TREATMENT,
        displayName: 'Dog - Toltrazuril - Treatment'
      },
      {
        animalType: AnimalType.CAT,
        medType: MedType.TOLTRAZURIL,
        indicationType: IndicationType.TREATMENT,
        displayName: 'Cat - Toltrazuril - Treatment'
      },
      {
        animalType: AnimalType.CHICKEN,
        medType: MedType.TOLTRAZURIL,
        indicationType: IndicationType.TREATMENT,
        displayName: 'Chicken - Toltrazuril - Treatment'
      },
      {
        animalType: AnimalType.CHICKEN,
        medType: MedType.TOLTRAZURIL,
        indicationType: IndicationType.PREVENTION,
        displayName: 'Chicken - Toltrazuril - Prevention'
      }
    ];
  }
}